import { supabase, insertRow } from './engine';
import { fmtMoneda } from '../utils/moneda';

// ──────────────────────────────────────────────────────────────────────────
// COMISIONES — un solo lugar para el cálculo de comisiones del corredor.
//
// REGLA DE NEGOCIO (no cambiar): la comisión POSITIVA nace del PAGO REAL del
// cliente (el cobro), nunca por cuota/letra ni por endoso. `generarComisionCobro`
// la genera al registrar un cobro, sobre el monto del cobro.
//
// EXCEPCIÓN autorizada por Ricardo: la comisión NEGATIVA (clawback) sí se genera
// al registrar un endoso negativo / cancelación / regularización-devolución, porque
// la aseguradora descuenta al corredor la comisión que ya había pagado sobre esa
// prima. `generarComisionNegativa` cubre ese caso (anclada por pago_id, no por cobro).
// ──────────────────────────────────────────────────────────────────────────

const r2 = n => Math.round((Number(n) || 0) * 100) / 100;

// Sobrecomisión (override): comisión EXTRA que la aseguradora paga APARTE de la
// comisión normal. Se marca con es_sobrecomision=true para separarla en totales y
// reportes. Configurable por póliza: % (sobre la prima cobrada, en cada cobro) o
// MONTO FIJO (una sola vez por póliza). Reparto: 100% RU o repartido con el agente.
// Es INDEPENDIENTE del comision_ramo (una póliza puede ser 0% normal y sí tener
// sobrecomisión). Idempotente: % por (cobro_id); monto fijo por (poliza_id).
export async function generarSobrecomision(polizaId, cobroId, monto) {
  const { data: poliza } = await supabase.from('polizas')
    .select('sobrecomision_tipo, sobrecomision_valor, sobrecomision_reparto, tipo, impuesto, subramos(base_comision)')
    .eq('id', polizaId).single();
  if (!poliza) return;
  const tipoSC  = poliza.sobrecomision_tipo;
  const valorSC = parseFloat(poliza.sobrecomision_valor) || 0;
  if (!tipoSC || valorSC <= 0) return;

  // Idempotencia
  if (tipoSC === 'monto') {
    const { data } = await supabase.from('comisiones').select('id')
      .eq('poliza_id', polizaId).eq('es_sobrecomision', true).limit(1);
    if ((data || []).length) return; // ya existe la fila única por póliza
  } else {
    const { data } = await supabase.from('comisiones').select('id')
      .eq('cobro_id', cobroId).eq('poliza_id', polizaId).eq('es_sobrecomision', true).limit(1);
    if ((data || []).length) return; // ya existe la fila de este cobro
  }

  // Monto total de la sobrecomisión
  let montoTotal, primaCol, primaBase;
  if (tipoSC === 'porcentaje') {
    const baseComision = poliza.subramos?.base_comision ?? 'sin_impuesto';
    const baseCalculo = baseComision === 'con_impuesto'
      ? (parseFloat(monto) || 0)
      : (parseFloat(monto) || 0) / (1 + (poliza.impuesto == null ? 5 : (parseFloat(poliza.impuesto) || 0)) / 100);
    montoTotal = baseCalculo * valorSC / 100;
    primaCol   = parseFloat(monto) || 0;
    primaBase  = r2(baseCalculo);
  } else { // monto fijo, una sola vez por póliza
    montoTotal = valorSC;
    primaCol   = 0;
    primaBase  = 0;
  }
  if (r2(montoTotal) === 0) return;

  const periodo = new Date().toISOString().slice(0, 7);
  const base = {
    poliza_id: polizaId, cobro_id: cobroId, es_sobrecomision: true,
    tipo: poliza.tipo || 'nueva', prima: primaCol, prima_base: primaBase,
    periodo, estado: 'pendiente_aseg',
  };

  // Reparto: 'agente' → mismo split de la póliza; cualquier otro → 100% RU.
  if ((poliza.sobrecomision_reparto || 'ru') === 'agente') {
    const { data: agentes } = await supabase.from('poliza_agentes')
      .select('agente_id, porcentaje').eq('poliza_id', polizaId);
    if (agentes?.length) {
      const sumAgPct   = agentes.reduce((s, a) => s + (parseFloat(a.porcentaje) || 0), 0);
      const pctRUFinal = Math.max(0, 100 - sumAgPct);
      for (let i = 0; i < agentes.length; i++) {
        const pctAg = parseFloat(agentes[i].porcentaje) || 0;
        await insertRow('comisiones', {
          ...base, agente_id: agentes[i].agente_id,
          porcentaje_agente: pctAg, porcentaje_ru: i === 0 ? pctRUFinal : 0,
          monto_agente: r2(montoTotal * pctAg / 100),
          monto_ru:     i === 0 ? r2(montoTotal * pctRUFinal / 100) : 0,
        });
      }
      return;
    }
    // sin agentes → cae a 100% RU
  }
  await insertRow('comisiones', {
    ...base, agente_id: null,
    porcentaje_agente: 0, porcentaje_ru: 100,
    monto_agente: 0, monto_ru: r2(montoTotal),
  });
}

// Comisión POSITIVA sobre un cobro real del cliente. Idempotente por (cobro_id,
// poliza_id, agente): cuando el saco cruza vigencias, un mismo cobro se reparte
// entre varias pólizas y cada porción lleva su propia comisión (ver generarComisionesDelCobro).
export async function generarComisionCobro(polizaId, cobroId, monto) {
  // La sobrecomisión va aparte y es independiente del comision_ramo (se genera
  // aunque la póliza tenga 0% normal). Por eso va antes de cualquier early-return.
  await generarSobrecomision(polizaId, cobroId, monto);

  const [{ data: poliza }, { data: agentes }, { data: comExist }] = await Promise.all([
    supabase.from('polizas').select('comision_ramo, tipo, impuesto, referido_id, referido_comision_pct, subramos(base_comision)').eq('id', polizaId).single(),
    supabase.from('poliza_agentes').select('agente_id, porcentaje').eq('poliza_id', polizaId),
    // Solo las comisiones NORMALES cuentan para "ya existe": la sobrecomisión recién
    // generada arriba es una fila aparte y, si se contaba, hacía saltar la comisión normal
    // (la del agente cuando se reparte con él, o la del corretaje en pólizas sin agente).
    supabase.from('comisiones').select('agente_id, referido_id').eq('cobro_id', cobroId).eq('poliza_id', polizaId).eq('es_sobrecomision', false),
  ]);
  if (!poliza || !poliza.comision_ramo) return;
  const agenteIdsConCom = new Set((comExist || []).map(c => c.agente_id));
  const baseComision = poliza.subramos?.base_comision ?? 'sin_impuesto';
  const baseCalculo = baseComision === 'con_impuesto'
    ? (parseFloat(monto) || 0)
    : (parseFloat(monto) || 0) / (1 + (poliza.impuesto == null ? 5 : (parseFloat(poliza.impuesto) || 0)) / 100);
  const comisionRU = baseCalculo * (parseFloat(poliza.comision_ramo) || 0) / 100;
  const periodo    = new Date().toISOString().slice(0, 7);

  // Comisión del REFERIDO (referidor): fila ADICIONAL e independiente de agentes/RU.
  // El referido cobra en NUEVA y en RENOVACIÓN, con el % pactado en la póliza
  // (referido_comision_pct, que se setea según el tipo desde el catálogo del referido).
  // Solo si la póliza tiene referido y %; idempotente por (cobro_id, referido_id).
  if (poliza.referido_id && poliza.referido_comision_pct != null) {
    const pctRef = parseFloat(poliza.referido_comision_pct) || 0;
    const yaTieneRef = (comExist || []).some(c => c.referido_id === poliza.referido_id);
    if (pctRef > 0 && !yaTieneRef) {
      await insertRow('comisiones', {
        poliza_id: polizaId, cobro_id: cobroId, referido_id: poliza.referido_id, agente_id: null,
        tipo: poliza.tipo || 'nueva', prima: parseFloat(monto) || 0, prima_base: r2(baseCalculo),
        porcentaje_agente: pctRef, porcentaje_ru: 0,
        monto_agente: r2(baseCalculo * pctRef / 100), monto_ru: 0,
        // MISMA lógica que el agente: nace 'pendiente_aseg' y avanza a 'aprobado' cuando
        // la aseguradora le paga a RU; luego se liquida con el mismo flujo.
        periodo, estado: 'pendiente_aseg',
      });
    }
  }

  // Sin agente: la comisión es 100% de RU (corredor).
  // La fila de RU es la que tiene agente_id null Y referido_id null (no la del referido).
  if (!agentes?.length) {
    if ((comExist || []).some(c => c.agente_id == null && c.referido_id == null)) return;
    await insertRow('comisiones', {
      poliza_id: polizaId, cobro_id: cobroId, agente_id: null,
      tipo: poliza.tipo || 'nueva', prima: parseFloat(monto) || 0, prima_base: r2(baseCalculo),
      porcentaje_agente: 0, porcentaje_ru: 100,
      monto_agente: 0, monto_ru: r2(comisionRU),
      periodo, estado: 'pendiente_aseg',
    });
    return;
  }

  const sumAgPct   = agentes.reduce((s, a) => s + (parseFloat(a.porcentaje) || 0), 0);
  const pctRUFinal = Math.max(0, 100 - sumAgPct);
  for (let i = 0; i < agentes.length; i++) {
    if (agenteIdsConCom.has(agentes[i].agente_id)) continue;
    const pctAg   = parseFloat(agentes[i].porcentaje) || 0;
    await insertRow('comisiones', {
      poliza_id: polizaId, cobro_id: cobroId, agente_id: agentes[i].agente_id,
      tipo: poliza.tipo || 'nueva', prima: parseFloat(monto) || 0, prima_base: r2(baseCalculo),
      porcentaje_agente: pctAg, porcentaje_ru: i === 0 ? pctRUFinal : 0,
      monto_agente: r2(comisionRU * pctAg / 100),
      monto_ru:     i === 0 ? r2(comisionRU * pctRUFinal / 100) : 0,
      periodo, estado: 'pendiente_aseg',
    });
  }
}

// Genera las comisiones de UN cobro repartiéndolo entre las vigencias que realmente
// cobró. Es la forma correcta de comisionar: la comisión sale del dinero recibido, y
// con el saco cross-vigencia ese dinero puede saldar cuotas de la vigencia anterior.
//
// Ejemplo: entran B/.500 en la vigencia 2026 de una póliza renovada con arrastre. El
// saco cubre 2 cuotas de B/.100 que quedaron debiéndose en 2025 y 3 en 2026 → se
// generan DOS comisiones: B/.200 contra la póliza 2025 y B/.300 contra la 2026. Cada
// una con el `tipo` (nueva/renovación), % de ramo, agentes y referido de SU póliza,
// que es lo que define cuánto se comisiona.
//
// Llamar SIEMPRE después de `reaplicarSacoPoliza`, que es quien reconstruye el
// reparto en `cobro_cuotas`. Si el cobro no quedó aplicado a ninguna cuota (p. ej.
// la póliza estaba en saldo cero), se comisiona el monto completo contra `polizaId`,
// que es el comportamiento de siempre.
export async function generarComisionesDelCobro(cobroId, polizaId, monto) {
  const { data: enlaces } = await supabase.from('cobro_cuotas')
    .select('pago_id, monto_aplicado').eq('cobro_id', cobroId);

  const pagoIds = [...new Set((enlaces || []).map(e => e.pago_id).filter(Boolean))];
  if (!pagoIds.length) return generarComisionCobro(polizaId, cobroId, monto);

  const { data: cuotas } = await supabase.from('pagos')
    .select('id, poliza_id').in('id', pagoIds);
  const polizaDeCuota = {};
  for (const c of (cuotas || [])) polizaDeCuota[c.id] = c.poliza_id;

  // Cuánto de este cobro terminó pagando cada vigencia
  const porPoliza = {};
  for (const e of (enlaces || [])) {
    const pid = polizaDeCuota[e.pago_id];
    if (!pid) continue;
    porPoliza[pid] = r2((porPoliza[pid] || 0) + (parseFloat(e.monto_aplicado) || 0));
  }
  const vigencias = Object.keys(porPoliza).filter(pid => porPoliza[pid] > 0.005);
  if (!vigencias.length) return generarComisionCobro(polizaId, cobroId, monto);

  // Una sola vigencia: el camino de siempre, sin cambio de comportamiento.
  if (vigencias.length === 1) {
    return generarComisionCobro(Number(vigencias[0]), cobroId, porPoliza[vigencias[0]]);
  }

  // Varias: la comisión se parte. Se procesan en orden para que el reparto sea
  // estable y la vigencia más vieja (la que se cobra primero en el saco) mande.
  for (const pid of vigencias.sort((a, b) => Number(a) - Number(b))) {
    await generarComisionCobro(Number(pid), cobroId, porPoliza[pid]);
  }
}

// Comisión NEGATIVA (clawback) por un endoso/cancelación/devolución que reduce prima.
// `montoConImpuesto` debe ser NEGATIVO (la prima devuelta, con impuesto, en negativo).
// Se ancla por pago_id (la fila de saldo negativo creada por el endoso) — así
// `confirmAnularEndoso`, que limpia comisiones por pago_id, la revierte sin cambios.
// Idempotente por (pago_id, agente).
export async function generarComisionNegativa(polizaId, pagoId, montoConImpuesto, opts = {}) {
  const estado = opts.estado || 'pendiente_aseg';
  let monto    = parseFloat(montoConImpuesto) || 0;
  if (monto >= 0 || !pagoId) return; // solo clawbacks reales (monto < 0)

  // ── El clawback SOLO aplica sobre prima YA PAGADA (la que se le devuelve al cliente). ──
  // La comisión se gana/pierde con lo COBRADO: una cancelación o endoso negativo sobre prima
  // que NO se pagó no revierte comisión (RU nunca la cobró de la aseguradora). Se calcula el
  // SOBREPAGO real del cliente = lo pagado − lo que ahora debe (ya con la nota de crédito
  // aplicada en `pagos`), y se limita el clawback a ese sobrepago. Sin sobrepago → sin
  // comisión negativa (evita clawbacks fantasma en cancelaciones de prima no pagada).
  const [{ data: cobrosPol }, { data: cargosPol }] = await Promise.all([
    supabase.from('cobros_recibidos').select('monto, anulado').eq('poliza_id', polizaId),
    supabase.from('pagos').select('monto').eq('poliza_id', polizaId),
  ]);
  const pagado = r2((cobrosPol || []).filter(c => !c.anulado).reduce((s, c) => s + (parseFloat(c.monto) || 0), 0));
  const neto   = r2((cargosPol || []).reduce((s, c) => s + (parseFloat(c.monto) || 0), 0)); // incluye la nota de crédito
  const sobrepago = Math.max(0, r2(pagado - neto)); // lo que efectivamente se le devuelve al cliente
  monto = r2(-Math.min(Math.abs(monto), sobrepago)); // clawback capado al sobrepago (queda negativo)
  if (monto > -0.005) return; // no hay prima pagada que revertir → no se genera comisión negativa

  const [{ data: poliza }, { data: agentes }, { data: comExist }] = await Promise.all([
    supabase.from('polizas').select('comision_ramo, tipo, impuesto, subramos(base_comision)').eq('id', polizaId).single(),
    supabase.from('poliza_agentes').select('agente_id, porcentaje').eq('poliza_id', polizaId),
    supabase.from('comisiones').select('agente_id').eq('pago_id', pagoId),
  ]);
  if (!poliza || !poliza.comision_ramo) return;
  const agenteIdsConCom = new Set((comExist || []).map(c => c.agente_id));
  const baseComision = poliza.subramos?.base_comision ?? 'sin_impuesto';
  const baseCalculo = baseComision === 'con_impuesto'
    ? monto
    : monto / (1 + (poliza.impuesto == null ? 5 : (parseFloat(poliza.impuesto) || 0)) / 100);
  const comisionRU = baseCalculo * (parseFloat(poliza.comision_ramo) || 0) / 100; // negativo
  const periodo    = new Date().toISOString().slice(0, 7);

  if (!agentes?.length) {
    if ((comExist || []).some(c => c.agente_id == null)) return;
    await insertRow('comisiones', {
      poliza_id: polizaId, pago_id: pagoId, agente_id: null,
      tipo: poliza.tipo || 'nueva', prima: monto, prima_base: r2(baseCalculo),
      porcentaje_agente: 0, porcentaje_ru: 100,
      monto_agente: 0, monto_ru: r2(comisionRU),
      periodo, estado,
    });
    return;
  }

  const sumAgPct   = agentes.reduce((s, a) => s + (parseFloat(a.porcentaje) || 0), 0);
  const pctRUFinal = Math.max(0, 100 - sumAgPct);
  for (let i = 0; i < agentes.length; i++) {
    if (agenteIdsConCom.has(agentes[i].agente_id)) continue;
    const pctAg = parseFloat(agentes[i].porcentaje) || 0;
    await insertRow('comisiones', {
      poliza_id: polizaId, pago_id: pagoId, agente_id: agentes[i].agente_id,
      tipo: poliza.tipo || 'nueva', prima: monto, prima_base: r2(baseCalculo),
      porcentaje_agente: pctAg, porcentaje_ru: i === 0 ? pctRUFinal : 0,
      monto_agente: r2(comisionRU * pctAg / 100),
      monto_ru:     i === 0 ? r2(comisionRU * pctRUFinal / 100) : 0,
      periodo, estado,
    });
  }
}

// Ajuste por corrección del PORCENTAJE de comisión.
//
// Al cambiar el % de una póliza, las comisiones que todavía están en
// 'pendiente_aseg' se borran y se regeneran con el % nuevo (lo hace Polizas.jsx).
// Pero las que ya avanzaron —'aprobado' o 'pagado'— NO se pueden reescribir: al
// agente ya se le liquidó ese dinero. Borrarlas o cambiarles el monto haría que la
// liquidación dejara de cuadrar con lo que de verdad salió de caja.
//
// Lo correcto es dejar la comisión pagada como está y registrar la DIFERENCIA como
// una fila nueva, que entra a la próxima liquidación: positiva si se le quedó
// debiendo al agente, negativa (clawback) si se le pagó de más.
//
//   Ejemplo: se cobró con 15% y el correcto era 10%. Una comisión ya pagada sobre
//   una base de B/.1000 valía B/.150 y debía valer B/.100 → se genera un ajuste de
//   B/.-50 que se descuenta en la siguiente liquidación.
//
// Idempotente: cada ajuste se marca en `notas` con [ajuste-pct:<id de la original>]
// y no se vuelve a generar para esa comisión.
export async function ajustarComisionesPorCambioDePct(polizaId, cambios = {}) {
  const { ramoAntes, ramoDespues, refAntes, refDespues } = cambios;
  const cambioRamo = ramoAntes != null && ramoDespues != null && Math.abs(Number(ramoDespues) - Number(ramoAntes)) > 0.0001;
  const cambioRef  = refAntes  != null && refDespues  != null && Math.abs(Number(refDespues)  - Number(refAntes))  > 0.0001;
  if (!cambioRamo && !cambioRef) return [];

  // Solo las que ya no se regeneran. Las anuladas y las sobrecomisiones quedan fuera:
  // la sobrecomisión no depende del % del ramo (tiene su propio valor).
  const { data: yaPagadas } = await supabase.from('comisiones')
    .select('id, agente_id, referido_id, tipo, prima, prima_base, porcentaje_agente, porcentaje_ru, cobro_id, periodo, estado')
    .eq('poliza_id', polizaId)
    .in('estado', ['aprobado', 'pagado'])
    .eq('es_sobrecomision', false)
    .is('anulado_en', null);
  if (!yaPagadas?.length) return [];

  // Ajustes ya generados antes, para no duplicarlos si se guarda dos veces.
  const { data: previos } = await supabase.from('comisiones')
    .select('notas').eq('poliza_id', polizaId).not('notas', 'is', null);
  const yaAjustadas = new Set();
  for (const p of (previos || [])) {
    const m = String(p.notas).match(/\[ajuste-pct:(\d+)\]/g) || [];
    for (const t of m) yaAjustadas.add(t.replace(/\D/g, ''));
  }

  const periodo = new Date().toISOString().slice(0, 7);
  const creados = [];

  for (const c of yaPagadas) {
    if (yaAjustadas.has(String(c.id))) continue;
    const esDeReferido = c.referido_id != null;
    if (esDeReferido ? !cambioRef : !cambioRamo) continue;

    const antes   = Number(esDeReferido ? refAntes   : ramoAntes);
    const despues = Number(esDeReferido ? refDespues : ramoDespues);
    const base    = parseFloat(c.prima_base) || 0;
    if (base <= 0) continue;

    // La diferencia se reparte igual que la comisión original.
    const deltaTotal = r2(base * (despues - antes) / 100);
    if (Math.abs(deltaTotal) < 0.005) continue;
    const dAgente = esDeReferido
      ? deltaTotal
      : r2(deltaTotal * (parseFloat(c.porcentaje_agente) || 0) / 100);
    const dRU = esDeReferido
      ? 0
      : r2(deltaTotal * (parseFloat(c.porcentaje_ru) || 0) / 100);
    if (Math.abs(dAgente) < 0.005 && Math.abs(dRU) < 0.005) continue;

    const signo = deltaTotal < 0 ? 'a favor del corretaje' : 'a favor del agente';
    const fila = await insertRow('comisiones', {
      poliza_id: polizaId, cobro_id: c.cobro_id || null,
      agente_id: c.agente_id ?? null, referido_id: c.referido_id ?? null,
      tipo: c.tipo || 'nueva',
      prima: 0, prima_base: base,
      porcentaje_agente: parseFloat(c.porcentaje_agente) || 0,
      porcentaje_ru: parseFloat(c.porcentaje_ru) || 0,
      monto_agente: dAgente, monto_ru: dRU,
      periodo, estado: 'pendiente_aseg',
      notas: `Ajuste por corrección del % de ${esDeReferido ? 'referido' : 'comisión'} `
           + `(${antes}% → ${despues}%) sobre una comisión ya ${c.estado} — ${signo}. `
           + `[ajuste-pct:${c.id}]`,
    }).catch(() => null);
    if (fila) creados.push(fila);
  }
  return creados;
}

// ──────────────────────────────────────────────────────────────────────────
// AJUSTES IDENTIFICABLES
//
// REGLA: una comisión que ya avanzó (la aseguradora la pagó, se aprobó o se le
// liquidó al agente) NO se borra ni se reescribe: se NETEA con una fila aparte que
// dice a qué se refiere. Esa explicación vive en `notas` junto a una marca entre
// corchetes, que es lo que hace idempotente al ajuste y lo que la pantalla y la
// liquidación impresa usan para mostrarle al agente el porqué del monto.
// ──────────────────────────────────────────────────────────────────────────
const RE_MARCA = /\s*\[(ajuste-pct|ajuste-saco|ajuste-cobro|anulacion-cobro):[^\]]*\]/g;

// Texto legible de un ajuste (sin la marca técnica). '' si la comisión no es un ajuste.
export function conceptoDeAjuste(notas) {
  if (!notas) return '';
  const linea = String(notas).split('\n').find(l => /\[(ajuste-pct|ajuste-saco|ajuste-cobro|anulacion-cobro):/.test(l));
  return linea ? linea.replace(RE_MARCA, '').trim() : '';
}

// "Libre" = nadie ha movido dinero sobre ella todavía → se puede borrar y regenerar.
const esLibre = c => c.estado === 'pendiente_aseg' && Math.abs(parseFloat(c.monto_recibido_aseg) || 0) < 0.005;

const fechaCorta = f => { const [y, m, d] = String(f || '').slice(0, 10).split('-'); return d ? `${d}/${m}/${y}` : '—'; };
const etiquetaCobro = (co, conMonto = true) => {
  const recibo = co.num_recibo_ru || co.num_recibo_aseg;
  return `${recibo ? 'recibo ' + recibo : '#' + co.id} del ${fechaCorta(co.fecha)}`
    + (conMonto ? ` por ${fmtMoneda(co.monto)}` : '');
};
// A quién le toca la fila: un agente, un referido o solo el corretaje.
const llaveDe = c => (c.referido_id != null ? `r:${c.referido_id}` : c.agente_id != null ? `a:${c.agente_id}` : 'ru');

// ANULAR UN COBRO sin perder lo que ya se pagó.
//
// Antes se borraban TODAS las comisiones del cobro, también las ya liquidadas: el
// agente se quedaba con el dinero, la comisión desaparecía y la liquidación dejaba de
// cuadrar con lo que salió de caja. Ahora:
//   · las que nadie ha tocado ('pendiente_aseg' sin nada recibido) se borran, como siempre;
//   · las demás SE QUEDAN y cada una recibe su contra-asiento en negativo, que dice de
//     qué cobro es la anulación. Nace en 'pendiente' (Pendiente Agente): del lado de la
//     aseguradora no hay nada que esperar, así que va directo a la cola del agente y se
//     netea en su próxima liquidación.
// Idempotente por la marca [anulacion-cobro:<cobro>:<comisión original>].
export async function revertirComisionesDeCobroAnulado(cobro, { motivo, usuario } = {}) {
  if (!cobro?.id) return { borradas: 0, revertidas: 0 };
  const { data: filas } = await supabase.from('comisiones').select('*').eq('cobro_id', cobro.id);
  const vivas = (filas || []).filter(c => c.estado !== 'anulada');

  const libres = vivas.filter(esLibre);
  if (libres.length) await supabase.from('comisiones').delete().in('id', libres.map(c => c.id));

  const reMarca = new RegExp(`\\[anulacion-cobro:${cobro.id}:(\\d+)\\]`, 'g');
  const yaRevertidas = new Set();
  for (const c of vivas) for (const m of String(c.notas || '').matchAll(reMarca)) yaRevertidas.add(m[1]);

  const hoy = new Date().toISOString().slice(0, 10);
  let revertidas = 0;
  for (const c of vivas) {
    if (esLibre(c)) continue;
    if (/\[anulacion-cobro:/.test(c.notas || '')) continue;      // ya es un contra-asiento
    if (yaRevertidas.has(String(c.id))) continue;
    // La sobrecomisión de MONTO FIJO es una sola por póliza, no sale del dinero de este cobro.
    if (c.es_sobrecomision && Math.abs(parseFloat(c.prima) || 0) < 0.005) continue;
    const mAg = r2(-(parseFloat(c.monto_agente) || 0)), mRu = r2(-(parseFloat(c.monto_ru) || 0));
    if (Math.abs(mAg) < 0.005 && Math.abs(mRu) < 0.005) continue;

    const sigueEsperando = c.estado === 'pendiente_aseg'; // la aseguradora solo pagó una parte
    await insertRow('comisiones', {
      poliza_id: c.poliza_id, cobro_id: cobro.id,
      agente_id: c.agente_id ?? null, referido_id: c.referido_id ?? null,
      tipo: c.tipo || 'nueva', es_sobrecomision: !!c.es_sobrecomision,
      prima: r2(-(parseFloat(c.prima) || 0)),
      prima_base: c.prima_base == null ? null : r2(-(parseFloat(c.prima_base) || 0)),
      porcentaje_agente: parseFloat(c.porcentaje_agente) || 0,
      porcentaje_ru: parseFloat(c.porcentaje_ru) || 0,
      monto_agente: mAg, monto_ru: mRu,
      monto_recibido_aseg: r2(-(parseFloat(c.monto_recibido_aseg) || 0)),
      periodo: hoy.slice(0, 7),
      estado: sigueEsperando ? 'pendiente_aseg' : 'pendiente',
      fecha_recibio_comision: sigueEsperando ? null : hoy,
      notas: `Anulación del cobro ${etiquetaCobro(cobro)}`
           + (motivo ? ` — motivo: ${motivo}` : '')
           + `. Revierte una comisión que ya estaba ${c.estado === 'pagado' ? 'pagada' : 'en proceso'}`
           + (usuario ? ` (anuló ${usuario})` : '') + `. [anulacion-cobro:${cobro.id}:${c.id}]`,
    });
    revertidas++;
  }
  return { borradas: libres.length, revertidas };
}

// ── Piezas compartidas para MOVER dinero comisionable de un cobro en una vigencia ──
// Las usan el saco (un cobro cambió de vigencia) y la corrección del monto de un cobro.

// Cuánto dinero de un cobro tienen atribuido hoy las comisiones de una vigencia.
// Cada destinatario lleva el monto completo en `prima`, así que se mira uno solo.
const atribuidoDe = fs => {
  if (!fs.length) return 0;
  const guia = llaveDe(fs.find(f => f.referido_id == null) || fs[0]);
  return r2(fs.filter(f => llaveDe(f) === guia).reduce((s, f) => s + (parseFloat(f.prima) || 0), 0));
};

// Decide el CAMINO para mover `aplicar` (±) en las comisiones de (cobro, póliza):
//   'nueva'        → esa vigencia no tenía comisión de este cobro: se genera normal;
//   'libre'        → nadie ha movido dinero sobre ellas: se borran y regeneran;
//   'comprometida' → ya avanzaron: no se tocan, se agrega una fila de ajuste.
// Si la sobrecomisión de esa vigencia ya avanzó, tampoco se regenera (iría por el mismo
// generador): todo el paso va por ajuste.
function armarPaso(delCobro, polId, aplicar, extra = {}) {
  const dePoliza = delCobro.filter(f => f.poliza_id === polId);
  const normales = dePoliza.filter(f => !f.es_sobrecomision);
  const sobreFirme = dePoliza.some(f => f.es_sobrecomision && !esLibre(f));
  const ruta = !normales.length ? 'nueva'
    : (normales.every(esLibre) && !sobreFirme) ? 'libre' : 'comprometida';
  return { ...extra, polId, aplicar, normales, atribuido: atribuidoDe(normales), ruta };
}

// Ejecuta un paso. `notaAjuste` es el texto (con su marca) de las filas de ajuste.
async function ejecutarPaso(paso, cobroId, delCobro, notaAjuste) {
  const creados = [];
  if (paso.ruta === 'nueva') {
    if (paso.aplicar > 0) await generarComisionCobro(paso.polId, cobroId, paso.aplicar);
    return creados;
  }
  if (paso.ruta === 'libre') {
    // También la sobrecomisión por % de esta vigencia (libre): se regenera sola.
    const sobre = delCobro.filter(f => f.poliza_id === paso.polId && f.es_sobrecomision
      && esLibre(f) && Math.abs(parseFloat(f.prima) || 0) > 0.005);
    await supabase.from('comisiones').delete().in('id', [...paso.normales, ...sobre].map(f => f.id));
    const nuevoMonto = r2(paso.atribuido + paso.aplicar);
    if (nuevoMonto > 0.005) await generarComisionCobro(paso.polId, cobroId, nuevoMonto);
    return creados;
  }
  // Comprometida: una fila de ajuste por destinatario, a la tasa con la que de verdad
  // se le comisionó este cobro (incluye las correcciones de % ya ajustadas).
  const periodo = new Date().toISOString().slice(0, 7);
  for (const k of [...new Set(paso.normales.map(llaveDe))]) {
    const mias = paso.normales.filter(f => llaveDe(f) === k);
    const conPrima = mias.filter(f => (parseFloat(f.prima) || 0) > 0.005);
    const sinPrima = mias.filter(f => Math.abs(parseFloat(f.prima) || 0) < 0.005); // ajustes de %
    const sumPrima = conPrima.reduce((s, f) => s + (parseFloat(f.prima) || 0), 0);
    if (sumPrima < 0.005) continue;
    const suma = campo => [...conPrima, ...sinPrima].reduce((s, f) => s + (parseFloat(f[campo]) || 0), 0);
    const sumBase = conPrima.reduce((s, f) => s + (parseFloat(f.prima_base) || 0), 0);
    const mAg = r2(paso.aplicar * suma('monto_agente') / sumPrima);
    const mRu = r2(paso.aplicar * suma('monto_ru') / sumPrima);
    if (Math.abs(mAg) < 0.005 && Math.abs(mRu) < 0.005) continue;
    const molde = conPrima[0];
    const fila = await insertRow('comisiones', {
      poliza_id: paso.polId, cobro_id: cobroId,
      agente_id: molde.agente_id ?? null, referido_id: molde.referido_id ?? null,
      tipo: molde.tipo || 'nueva',
      prima: paso.aplicar, prima_base: r2(paso.aplicar * sumBase / sumPrima),
      porcentaje_agente: parseFloat(molde.porcentaje_agente) || 0,
      porcentaje_ru: parseFloat(molde.porcentaje_ru) || 0,
      monto_agente: mAg, monto_ru: mRu,
      periodo, estado: 'pendiente_aseg',
      notas: notaAjuste,
    }).catch(() => null);
    if (fila) creados.push(fila);
  }
  return creados;
}

// EL SACO MOVIÓ UN COBRO ENTRE VIGENCIAS → sus comisiones lo siguen.
//
// `generarComisionesDelCobro` reparte la comisión según cómo quedó el cobro el día que
// entró. Si después se reaplica el saco (se anula un cobro anterior, entra un endoso) y
// ese cobro pasa a cubrir más de una vigencia y menos de otra, la división vieja se
// quedaba para siempre — y si las vigencias tienen % distintos, el total queda mal.
//   Ejemplo: 210/290 al 25%/15% → si pasa a 315/185 hay B/.10 de comisión sin registrar.
//
// La llama `reaplicarSacoPoliza` al final, SOLO cuando el grupo tiene varias vigencias.
//   · Solo mira el TRASLADO entre vigencias (lo que una gana y otra pierde). Si un cobro
//     pasa a cubrir menos EN TOTAL (p. ej. un endoso que reduce prima) no es asunto de
//     aquí: de eso se encarga el clawback, y hacerlo dos veces descontaría doble.
//   · Trabaja sobre lo que CAMBIÓ en esta corrida (reparto de antes vs. de ahora), no
//     sobre una comparación general: no "corrige" de golpe el historial anterior a la regla.
//   · Nunca mueve más de lo que falta para llegar al reparto real: si una corrida
//     anterior quedó a medias, repetirla no duplica nada.
//   · Si las vigencias comisionan IGUAL (mismo %, agentes y referido) y hay comisiones ya
//     comprometidas, el neto es cero para todos → no se genera nada (decisión de Ricardo:
//     dos filas que se cancelan son ruido).
export async function reconciliarComisionesConSaco({ cobros, repartoAntes = {}, repartoAhora = {} }) {
  const cambios = [];
  for (const co of (cobros || [])) {
    const a = repartoAntes[co.id] || {}, b = repartoAhora[co.id] || {};
    const deCobro = [];
    for (const pid of new Set([...Object.keys(a), ...Object.keys(b)])) {
      const delta = r2((b[pid] || 0) - (a[pid] || 0));
      if (Math.abs(delta) > 0.005) deCobro.push({ cobro: co, polId: Number(pid), antes: a[pid] || 0, ahora: b[pid] || 0, delta });
    }
    // Solo el traslado: lo que sube en unas vigencias contra lo que baja en otras.
    const sube = deCobro.filter(d => d.delta > 0).reduce((s, d) => s + d.delta, 0);
    const baja = deCobro.filter(d => d.delta < 0).reduce((s, d) => s - d.delta, 0);
    const traslado = Math.min(sube, baja);
    if (traslado < 0.005) continue;
    for (const d of deCobro) {
      cambios.push({ ...d, delta: r2(d.delta * traslado / (d.delta > 0 ? sube : baja)) });
    }
  }
  if (!cambios.length) return [];

  const cobroIds = [...new Set(cambios.map(c => c.cobro.id))];
  const { data: filasRaw } = await supabase.from('comisiones').select('*').in('cobro_id', cobroIds);
  const filas = (filasRaw || []).filter(f => f.estado !== 'anulada');
  if (!filas.length) return [];

  // Plan por cobro: qué mover en cada vigencia y por qué camino.
  const planes = [];
  for (const cobroId of cobroIds) {
    const delCobro = filas.filter(f => f.cobro_id === cobroId);
    if (!delCobro.length) continue; // cobro aún sin comisionar (lo hará quien lo registró) o póliza sin comisión
    const pasos = [];
    for (const cb of cambios.filter(c => c.cobro.id === cobroId)) {
      const atribuido = atribuidoDe(delCobro.filter(f => f.poliza_id === cb.polId && !f.es_sobrecomision));
      const falta = r2(cb.ahora - atribuido);
      const aplicar = r2(cb.delta > 0 ? Math.min(cb.delta, Math.max(falta, 0)) : Math.max(cb.delta, Math.min(falta, 0)));
      if (Math.abs(aplicar) < 0.005) continue;
      pasos.push(armarPaso(delCobro, cb.polId, aplicar, { cobro: cb.cobro, antes: cb.antes, ahora: cb.ahora }));
    }
    if (pasos.length) planes.push({ cobroId, delCobro, pasos });
  }
  if (!planes.length) return [];

  // ¿Las vigencias comisionan igual? Solo se consulta si hay algo comprometido que ajustar.
  const firmas = {};
  if (planes.some(p => p.pasos.some(s => s.ruta === 'comprometida'))) {
    const polIds = [...new Set(planes.flatMap(p => p.pasos.map(s => s.polId)))];
    const [{ data: pols }, { data: ags }] = await Promise.all([
      supabase.from('polizas').select('id, comision_ramo, impuesto, referido_id, referido_comision_pct, subramos(base_comision)').in('id', polIds),
      supabase.from('poliza_agentes').select('poliza_id, agente_id, porcentaje').in('poliza_id', polIds),
    ]);
    for (const p of (pols || [])) {
      firmas[p.id] = JSON.stringify([
        Number(p.comision_ramo) || 0,
        p.impuesto == null ? 5 : (Number(p.impuesto) || 0),
        p.subramos?.base_comision ?? 'sin_impuesto',
        p.referido_id || null,
        p.referido_id ? (Number(p.referido_comision_pct) || 0) : 0,
        (ags || []).filter(a => a.poliza_id === p.id)
          .map(a => [a.agente_id, Number(a.porcentaje) || 0]).sort((x, y) => x[0] - y[0]),
      ]);
    }
  }

  const creados = [];
  for (const plan of planes) {
    if (plan.pasos.some(s => s.ruta === 'comprometida')) {
      const fs = plan.pasos.map(s => firmas[s.polId]);
      const mismaFirma = fs.every(f => f && f === fs[0]);
      const neto = r2(plan.pasos.reduce((s, x) => s + x.aplicar, 0));
      if (mismaFirma && Math.abs(neto) < 0.01) continue; // traslado neutro: nadie gana ni pierde
    }
    for (const paso of plan.pasos) {
      const nota = `Ajuste por reaplicación de pagos: el cobro ${etiquetaCobro(paso.cobro)} ahora cubre `
        + `${fmtMoneda(paso.ahora)} de esta vigencia (antes ${fmtMoneda(paso.antes)}); `
        + `la comisión original ya estaba en proceso y no se reescribe. [ajuste-saco:${plan.cobroId}]`;
      creados.push(...await ejecutarPaso(paso, plan.cobroId, plan.delCobro, nota));
    }
  }
  return creados;
}

// SE CORRIGIÓ EL MONTO DE UN COBRO (se registraron 105 y eran 95) → su comisión lo sigue.
//
// Llamar DESPUÉS de actualizar el cobro y de `reaplicarSacoPoliza`. Compara lo que el
// cobro cubre AHORA en cada vigencia (mismo criterio que `generarComisionesDelCobro`:
// lo aplicado a cuotas; si no cubrió ninguna, el monto completo contra su póliza) contra
// lo que sus comisiones tienen atribuido, y mueve la diferencia: las libres se regeneran
// con el monto correcto; las que ya avanzaron reciben un ajuste [ajuste-cobro:<id>] que
// dice de cuánto a cuánto se corrigió. Idempotente: si ya cuadra, no hace nada.
export async function ajustarComisionesPorCorreccionDeCobro(cobro, { montoAntes, motivo, usuario } = {}) {
  if (!cobro?.id) return [];
  const { data: filasRaw } = await supabase.from('comisiones').select('*').eq('cobro_id', cobro.id);
  const delCobro = (filasRaw || []).filter(f => f.estado !== 'anulada');
  if (!delCobro.length) return []; // este cobro nunca comisionó (póliza sin % de comisión)

  const { data: enlaces } = await supabase.from('cobro_cuotas')
    .select('monto_aplicado, pagos(poliza_id)').eq('cobro_id', cobro.id);
  const esperado = {};
  for (const e of (enlaces || [])) {
    const pid = e.pagos?.poliza_id;
    if (pid) esperado[pid] = r2((esperado[pid] || 0) + (parseFloat(e.monto_aplicado) || 0));
  }
  if (!Object.keys(esperado).length && cobro.poliza_id) esperado[cobro.poliza_id] = r2(cobro.monto);

  const polIds = new Set([...Object.keys(esperado).map(Number), ...delCobro.filter(f => !f.es_sobrecomision).map(f => f.poliza_id)]);
  const nota = `Corrección del cobro ${etiquetaCobro(cobro, false)}: el monto pasó de `
    + `${fmtMoneda(montoAntes)} a ${fmtMoneda(cobro.monto)}`
    + (motivo ? ` — motivo: ${motivo}` : '') + (usuario ? ` (corrigió ${usuario})` : '')
    + `. La comisión original ya estaba en proceso y no se reescribe. [ajuste-cobro:${cobro.id}]`;

  const creados = [];
  for (const polId of polIds) {
    const atribuido = atribuidoDe(delCobro.filter(f => f.poliza_id === polId && !f.es_sobrecomision));
    const aplicar = r2((esperado[polId] || 0) - atribuido);
    if (Math.abs(aplicar) < 0.005) continue;
    creados.push(...await ejecutarPaso(armarPaso(delCobro, polId, aplicar), cobro.id, delCobro, nota));
  }
  return creados;
}

